import { h, Element } from "atomico";
import style from "./style.this.css";
import load from "./import-google-map";
import debounce from "./debounce";

class Position {
    constructor(location) {
        if (typeof location === "string") {
            let [lat, lng] = location.split(",").map(Number);
            location = { lat, lng };
        }
        this.lat =
            typeof location.lat === "function" ? location.lat() : location.lat;
        this.lng =
            typeof location.lng === "function" ? location.lng() : location.lng;
    }
    toString() {
        return this.lat + "," + this.lng;
    }
}

export class Tag extends Element {
    constructor() {
        super();
    }
    load() {
        return load({
            key: this.props.apiKey || this.props.key,
            libraries: "places"
        });
    }
    static get props() {
        return [
            "center",
            "zoom",
            "markers",
            "api-key",
            "autocomplete",
            "fullscreen"
        ];
    }
    async elementMount() {
        let { Map } = await this.load();

        this.map = new Map(this.querySelector(".container-map"), {
            center: new Position(
                this.props.center || "-34.1780483,-70.7584767"
            ),
            streetViewControl: false,
            mapTypeControl: false,
            zoom: this.props.zoom || 8,
            fullscreenControl: Boolean(this.props.fullscreen)
        });

        if (this.props.markers) this.setMarkers(this.props.markers);
        if (this.props.autocomplete)
            this.setAutocomplete(this.props.autocomplete);
    }
    async setMarker(data) {
        let { Marker, Animation } = await this.load();

        let config = {
            map: this.map,
            position: new Position(data.position),
            draggable: data.draggable,
            animation: Animation[data.animation || "DROP"]
        };

        if (data.title) config.title = data.title;
        if (data.label) config.label = data.label;
        if (data.icon) config.icon = data.icon;

        let marker = new Marker(config);

        ["click", "drag", "dragend"].forEach(type => {
            let listener = "on" + type;
            if (listener in data) marker.addListener(type, data[listener]);
        });

        return marker;
    }

    async setAutocomplete(autocomplete) {
        let { places, Geocoder } = await this.load(),
            input = new places.Autocomplete(
                this.querySelector(".autocomplete"),
                { types: ["geocode"] }
            ),
            process = debounce(1000, false),
            geocode = new Geocoder(),
            onchange = format =>
                autocomplete.onchange && autocomplete.onchange(format);

        if (autocomplete.restrictions) {
            input.setComponentRestrictions({
                country: autocomplete.restrictions
            });
        }

        if (autocomplete.fixCenter) {
            this.map.addListener("center_changed", () =>
                process(() => {
                    let center = this.map.getCenter();
                    geocode.geocode({ location: center }, (address, status) => {
                        onchange({
                            address,
                            position: new Position(center)
                        });
                    });
                })
            );
        }

        input.addListener("place_changed", () => {
            let place = input.getPlace();
            this.map.setZoom(17);
            this.map.setCenter(place.geometry.location);

            onchange({
                address: place.address_components,
                position: new Position(place.geometry.location)
            });
        });
    }

    setMarkers(markers) {
        markers = typeof markers === "string" ? SON.parse(markers) : markers;

        this._markers = (this._markers || Promise.resolve([]))
            .then(markers => markers.map(marker => marker.setMap(null)))
            .then(() =>
                Promise.all(markers.map(marker => this.setMarker(marker)))
            );
    }

    elementReceiveProps(change) {
        if (!this.map) return;
        if ("center" in change) {
            this.map.setCenter(new Position(change.center));
        }
        if ("zoom" in change) {
            this.map.setZoom(Number(change.zoom));
        }
        if ("markers" in change) {
            this.setMarkers(change.markers);
        }
    }

    render() {
        let autocomplete = this.props.autocomplete || {};
        return (
            <div class="container">
                {this.props.autocomplete ? (
                    <input
                        class={`autocomplete ${
                            autocomplete.center ? "-center" : ""
                        }`}
                    />
                ) : (
                    undefined
                )}
                <div class="container-map" />
                {autocomplete.fixCenter ? (
                    <div class="container-icon">
                        {this.slots["fix-icon"] || (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                style="isolation:isolate"
                                viewBox="1303 388.107 251 363"
                                width="26"
                            >
                                <ellipse
                                    vector-effect="non-scaling-stroke"
                                    rx="1"
                                    ry="1"
                                    transform="matrix(90.5 0 0 12 1428.5 739.107)"
                                    fill={autocomplete.iconColor || "#232329"}
                                    opacity=".3"
                                />
                                <path
                                    d="M1303 513.607c0-69.265 56.235-125.5 125.5-125.5s125.5 56.235 125.5 125.5q0 69.265-125.5 225.5-125.5-156.235-125.5-225.5z"
                                    fill={autocomplete.iconColor || "#232329"}
                                />
                                <circle
                                    vector-effect="non-scaling-stroke"
                                    r="1"
                                    transform="matrix(57 0 0 57 1428.5 505.41)"
                                    fill="#FFF"
                                />
                            </svg>
                        )}
                    </div>
                ) : (
                    undefined
                )}
            </div>
        );
    }
}

style("atom-google-map");

customElements.define("atom-google-map", Tag);
